import { supabase } from '../lib/supabase';

export type ActivityAction = 
  | 'create' 
  | 'update' 
  | 'delete' 
  | 'login' 
  | 'logout'
  | 'view'
  | 'export'
  | 'import'
  | 'approve'
  | 'reject'
  | 'assign'
  | 'unassign';

export type ResourceType =
  | 'load'
  | 'dispatch'
  | 'driver'
  | 'truck'
  | 'trailer'
  | 'customer'
  | 'invoice'
  | 'payment'
  | 'document'
  | 'user'
  | 'maintenance'
  | 'inspection'
  | 'accident'
  | 'claim'
  | 'settlement'
  | 'notification'
  | 'sms'
  | 'settings';

interface LogActivityParams {
  action: ActivityAction;
  resourceType: ResourceType;
  resourceId?: string;
  resourceName?: string;
  description?: string;
  oldData?: any;
  newData?: any;
}

/**
 * Log user activity to the database.
 * Writes directly to activity_logs (the old log_activity RPC is not deployed
 * in all environments, which caused 404s from supabase.rpc).
 */
export async function logActivity(params: LogActivityParams): Promise<void> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return; // RLS requires auth; silently skip for unauth'd contexts.

    // Best-effort lookup of the user's name/role for the log row.
    let userName: string | null = null;
    let userRole: string | null = null;
    const { data: profile } = await supabase
      .from('profiles')
      .select('full_name, first_name, last_name, role')
      .eq('id', user.id)
      .maybeSingle();
    if (profile) {
      userName = profile.full_name
        || `${profile.first_name || ''} ${profile.last_name || ''}`.trim()
        || null;
      userRole = profile.role || null;
    }

    await supabase.from('activity_logs').insert({
      user_id: user.id,
      user_name: userName,
      user_role: userRole,
      action: params.action,
      resource_type: params.resourceType,
      resource_id: params.resourceId || null,
      resource_name: params.resourceName || null,
      description: params.description || `${params.action} ${params.resourceType}`,
      old_data: params.oldData || null,
      new_data: params.newData || null,
    });
  } catch (error) {
    // Don't throw errors for logging failures - just log to console
    console.error('Failed to log activity:', error);
  }
}

/**
 * Get activity logs with filters
 */
export async function getActivityLogs(filters?: {
  userId?: string;
  action?: ActivityAction;
  resourceType?: ResourceType;
  startDate?: string;
  endDate?: string;
  limit?: number;
}) {
  try {
    let query = supabase
      .from('activity_logs')
      .select('*')
      .order('created_at', { ascending: false });

    if (filters?.userId) {
      query = query.eq('user_id', filters.userId);
    }

    if (filters?.action) {
      query = query.eq('action', filters.action);
    }

    if (filters?.resourceType) {
      query = query.eq('resource_type', filters.resourceType);
    }

    if (filters?.startDate) {
      query = query.gte('created_at', filters.startDate);
    }

    if (filters?.endDate) {
      query = query.lte('created_at', filters.endDate);
    }

    if (filters?.limit) {
      query = query.limit(filters.limit);
    } else {
      query = query.limit(100);
    }

    const { data, error } = await query;

    if (error) throw error;

    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Error fetching activity logs:', error);
    return { success: false, data: [], error };
  }
}

/**
 * Get activity statistics
 */
export async function getActivityStats(days: number = 7) {
  try {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);

    const { data, error } = await supabase
      .from('activity_logs')
      .select('action, resource_type, user_role')
      .gte('created_at', startDate.toISOString());

    if (error) throw error;

    // Calculate stats
    const stats = {
      totalActions: data?.length || 0,
      byAction: {} as Record<string, number>,
      byResourceType: {} as Record<string, number>,
      byUserRole: {} as Record<string, number>,
    };

    data?.forEach((log) => {
      stats.byAction[log.action] = (stats.byAction[log.action] || 0) + 1;
      stats.byResourceType[log.resource_type] = (stats.byResourceType[log.resource_type] || 0) + 1;
      if (log.user_role) {
        stats.byUserRole[log.user_role] = (stats.byUserRole[log.user_role] || 0) + 1;
      }
    });

    return { success: true, stats };
  } catch (error) {
    console.error('Error fetching activity stats:', error);
    return { success: false, stats: null, error };
  }
}
